import { useContext, useEffect, useMemo } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useHistory } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'

import elementsLocaleMapper from 'app/utils/elementsLocaleMapper'
import { STRIPE_PK } from 'app/consts'
import routes from 'app/routes'
import { useAppDispatch, useAppSelector } from 'app/hooks'

import { LocalizationContext } from 'components/i18n/LocalizationWrapper/LocalizationWrapper'
import AddPaymentMethodForm from 'features/home/components/HomeSubscription/SubscriptionManagement/AddPaymentMethodForm'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { createSetupIntentThunk } from 'features/home/components/HomeSubscription/slice'
import { setupIntentSelector } from 'features/home/components/HomeSubscription/selectors'
import { LoaderOverlay } from 'components/Loader'
import { currentUserSelector } from 'features/auth/login/selectors'
import type { AuthorizedUser } from 'app/types'
import {
  PaymentMethodsHeading,
  PaymentMethodsPageSubTitle,
  PaymentMethodsPageWrapper,
} from './styles'
import { H1 } from 'components/Typography'
import { paymentMethodsPageMessages } from './messages'
import { WhiteBlock } from 'components/Layout'
import {
  defaultPaymentMethodRequestSelectors,
  paymentMethodsListSelector,
  hasNoPaymentMethodsSelector,
} from './selectors'
import { getPaymentMethodsListThunk } from './slice'

function AddPaymentMethodPage() {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const rootDomainId = useAppSelector(currentRootDomainIdSelector)
  const localizationContext = useContext(LocalizationContext)
  const user: AuthorizedUser = useAppSelector(currentUserSelector)
  const isFetchingPaymentMethodsList = useAppSelector(
    paymentMethodsListSelector.isFetching
  )
  const isFetchingDefaultPaymentMethod = useAppSelector(
    defaultPaymentMethodRequestSelectors.isFetching
  )
  const isLoadingSetupIntent = useAppSelector(setupIntentSelector.isFetching)
  const hasNoPaymentMethods = useAppSelector(hasNoPaymentMethodsSelector)

  useEffect(() => {
    if (rootDomainId) {
      dispatch(createSetupIntentThunk(rootDomainId))
      dispatch(getPaymentMethodsListThunk(rootDomainId))
    }
  }, [dispatch, rootDomainId, user.id])

  const stripePromise = useMemo(
    () =>
      loadStripe(STRIPE_PK || '', {
        locale: elementsLocaleMapper(localizationContext.locale),
      }),
    [localizationContext.locale]
  )

  const handleAddPaymentMethodSuccess = () => {
    const paymentMethodsPath = routes.paymentMethods(rootDomainId)
    history.push(paymentMethodsPath)
  }

  if (
    isLoadingSetupIntent ||
    isFetchingPaymentMethodsList ||
    isFetchingDefaultPaymentMethod
  ) {
    return <LoaderOverlay isFullScreen />
  }

  return (
    <PaymentMethodsPageWrapper>
      <PaymentMethodsHeading>
        <H1>
          <FormattedMessage {...paymentMethodsPageMessages.title} />
        </H1>
      </PaymentMethodsHeading>
      <WhiteBlock>
        <PaymentMethodsPageSubTitle>
          <FormattedMessage {...paymentMethodsPageMessages.addMethod} />
        </PaymentMethodsPageSubTitle>
        <Elements key={localizationContext.locale} stripe={stripePromise}>
          <AddPaymentMethodForm
            isFirstMethod={hasNoPaymentMethods}
            onSuccess={handleAddPaymentMethodSuccess}
            isStandalone
          />
        </Elements>
      </WhiteBlock>
    </PaymentMethodsPageWrapper>
  )
}

export default AddPaymentMethodPage
